import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getVideoById, getVideoTypeName, relatedVideos, formatViews } from "@/lib/content";
import VideoCard from "@/components/base/VideoCard";
import ShareBar from "@/components/base/ShareBar";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function VideoDetailPage() {
  const { id = "" } = useParams();
  const item = getVideoById(id);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(30);

  usePageTitle(
    item ? `${item.title} - ${getVideoTypeName(item.type)} - 91.con` : "视频详情 - 91.con",
    item ? item.description : undefined,
  );

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center gap-4 px-4 py-24">
        <i className="ri-error-warning-line text-4xl text-primary-400"></i>
        <p className="text-foreground-700">未找到该视频，可能已被移除。</p>
        <Link
          to="/videos"
          className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
        >
          返回视频列表
        </Link>
      </div>
    );
  }

  const related = relatedVideos(item, 3);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/videos" className="transition-colors hover:text-primary-400">
          视频资源
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="line-clamp-1 text-foreground-800">{item.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          {/* 演示播放器 */}
          <div className="relative h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-[420px]">
            <img
              src={item.cover}
              alt={`${item.title} - 视频封面`}
              title={`${item.title} - 91.con娱乐视频`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-background-50/10 to-background-50/40"></div>

            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="absolute inset-0 flex items-center justify-center"
              aria-label={playing ? "暂停" : "播放"}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/90 transition-transform hover:scale-105">
                <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl text-background-50`}></i>
              </span>
            </button>

            <div className="absolute bottom-0 left-0 w-full p-4">
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-800">{playing ? "播放中" : "已暂停"}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-50/50">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="text-xs text-foreground-800">{item.duration}</span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                {getVideoTypeName(item.type)}
              </span>
              {item.hot && (
                <span className="flex items-center gap-1 rounded-sm bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-300">
                  <i className="ri-fire-fill"></i>热门
                </span>
              )}
              <span className="flex items-center gap-1 rounded-sm bg-background-200 px-2 py-0.5 text-xs text-foreground-700">
                <i className="ri-information-line"></i>演示播放器
              </span>
            </div>

            <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
              {item.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-primary-400"></i>时长 {item.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-400"></i>{item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-play-line text-primary-400"></i>{formatViews(item.views)} 次播放
              </span>
            </div>

            <p className="mt-5 text-base leading-relaxed text-foreground-800">{item.description}</p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-600"
                >
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-accent-300/40 bg-accent-100/40 p-4 text-sm text-foreground-800">
              <i className="ri-alert-line mr-1 text-accent-400"></i>
              视频播放为前端模拟演示，按钮暂无实际播放效果，待后端视频数据接口接入。
            </div>

            <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-foreground-600">
                <i className="ri-share-line mr-1 text-primary-400"></i>觉得还不错？分享给朋友
              </span>
              <ShareBar title={item.title} />
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-movie-line text-accent-400"></i>相关推荐
            </h3>
            <div className="flex flex-col gap-5">
              {related.length > 0 ? (
                related.map((r) => (
                  <VideoCard key={r.id} item={r} />
                ))
              ) : (
                <p className="text-sm text-foreground-500">暂无相关视频。</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}